/** Legacy note tags (slugs) → API subject name. */
export const LEGACY_SUBJECT_SLUG_TO_NAME = {
  polity: "Polity",
  economy: "Economy",
  geography: "Geography",
  governance: "Governance",
  "ethics-integrity": "Ethics",
  "sci-tech": "Science & Tech",
  security: "Internal Security",
  "intl-relations": "International Relations",
  "social-justice": "Society/Social Justice",
  "indian-society": "Society/Social Justice",
  environment: "Environment/Disaster Management",
  "disaster-management": "Environment/Disaster Management",
  history: "Modern History",
  constitution: "Polity",
  agriculture: "Agriculture",
  maths: "Maths",
  "general-upsc-preparation-strategy": "General UPSC Preparation Strategy",
};

const normalizeTagKey = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, "")
    .replace(/\//g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const buildSubjectNameSet = (subjectCatalog = []) => {
  const names = new Set();
  subjectCatalog.forEach((subject) => {
    if (subject?.name) {
      names.add(subject.name.trim().toLowerCase());
    }
  });
  return names;
};

export const getSubjectIdsForNames = (subjectNames = [], subjectCatalog = []) =>
  subjectNames
    .map((name) => {
      const match = subjectCatalog.find(
        (subject) => subject.name?.trim().toLowerCase() === name.trim().toLowerCase(),
      );
      return match?.id;
    })
    .filter((id) => id != null && id !== "essay");

export const resolveSubjectName = (tag, subjectCatalog = []) => {
  if (!tag) {
    return null;
  }

  const trimmed = String(tag).trim();
  const lower = trimmed.toLowerCase();

  const fromCatalog = subjectCatalog.find(
    (subject) => subject.name?.trim().toLowerCase() === lower,
  );
  if (fromCatalog) {
    return fromCatalog.name;
  }

  const legacyName = LEGACY_SUBJECT_SLUG_TO_NAME[normalizeTagKey(trimmed)];
  if (legacyName) {
    const legacyMatch = subjectCatalog.find(
      (subject) => subject.name?.trim().toLowerCase() === legacyName.toLowerCase(),
    );
    return legacyMatch?.name || legacyName;
  }

  return null;
};

export const isSubjectTag = (tag, subjectCatalog = []) =>
  Boolean(resolveSubjectName(tag, subjectCatalog));

export const resolveCanonicalTopicName = (tag, topicNames = []) => {
  const lower = String(tag).trim().toLowerCase();
  return topicNames.find((topic) => topic.toLowerCase() === lower) || null;
};

export const filterTopicsBySearch = (topics, searchTerm = "") => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) {
    return topics;
  }
  return topics.filter((topic) => topic.toLowerCase().includes(query));
};

export const partitionNoteTags = (tags = [], subjectCatalog = [], topicNames = []) => {
  const subjects = [];
  const topics = [];
  const custom = [];
  const knownSubjectNames = buildSubjectNameSet(subjectCatalog);
  const topicNameSet = new Set(topicNames.map((name) => name.toLowerCase()));

  tags.forEach((tag) => {
    if (!tag) {
      return;
    }

    const subjectName = resolveSubjectName(tag, subjectCatalog);
    if (
      subjectName &&
      (knownSubjectNames.size === 0 || knownSubjectNames.has(subjectName.toLowerCase()))
    ) {
      if (!subjects.some((name) => name.toLowerCase() === subjectName.toLowerCase())) {
        subjects.push(subjectName);
      }
      return;
    }

    if (knownSubjectNames.size === 0) {
      const legacyOnly = LEGACY_SUBJECT_SLUG_TO_NAME[normalizeTagKey(tag)];
      if (legacyOnly && !subjects.some((name) => name.toLowerCase() === legacyOnly.toLowerCase())) {
        subjects.push(legacyOnly);
        return;
      }
    }

    const canonicalTopic = resolveCanonicalTopicName(tag, topicNames);
    if (canonicalTopic || (topicNameSet.size > 0 && topicNameSet.has(String(tag).trim().toLowerCase()))) {
      const topicToAdd = canonicalTopic || tag;
      if (!topics.some((topic) => topic.toLowerCase() === topicToAdd.toLowerCase())) {
        topics.push(topicToAdd);
      }
      return;
    }

    if (!custom.includes(tag)) {
      custom.push(tag);
    }
  });

  return { subjects, topics, custom };
};

export const mergeNoteTags = ({ subjects = [], topics = [], custom = [] } = {}) => {
  const merged = [];
  const seen = new Set();

  [...subjects, ...topics, ...custom].forEach((tag) => {
    const key = String(tag).trim().toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(tag);
  });

  return merged;
};

export const pruneTopicsForSubjects = (topics, allowedTopicNames = []) => {
  const allowed = new Set(allowedTopicNames.map((topic) => topic.toLowerCase()));
  return topics.filter((topic) => allowed.has(topic.toLowerCase()));
};
